/**
 * Engine-hosted sales page for the BKOS License — a standalone, firm-level paid
 * subscription ($195 USD/month) to the BKOS platform (the Komunily-based community
 * at bkos.io that holds the Pure Bookkeeping templates).
 *
 * Rendered by ProgramSalesPage when slug === 'bkos-license' (mirrors how
 * slug === 'soda-intensive' → <SodaIntensivePage>). The parent has already
 * fetched /api/public/programs/bkos-license and applied the Pure Bookkeeping
 * palette + fonts to <html> as --prog-* / --font-* custom properties, so this
 * component just consumes them (with PB fallbacks).
 *
 * Brand: Pure Bookkeeping (temporary — TSB resells it; a dedicated b_bkos brand
 * exists for the eventual own-brand move). Voice: warm PB/TSB operator.
 *
 * Structure (single recurring offer, not a cohort, not a 1:1): hero → the problem
 * → what's inside BKOS → the platform / what the license includes → proof →
 * is this for you → how it works → FAQ → closing CTA. CTA opens a lightweight
 * buyer-intent capture, then navigates to /programs/bkos-license/order where the
 * Spiffy embed lives — the same checkout path every other program uses.
 *
 * NB: distinct from the free `bkos-founding-cohort` program. Fulfillment (the
 * bkos.io Komunily grant on purchase) is Phase 2; this page is the funnel only.
 *
 * COPY STATUS: first pass, structurally complete + on-brand. Flagged for a Lisa /
 * PB voice review before launch; real testimonials to be added when supplied
 * (none fabricated here).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProgramPublicResponse } from './ProgramSalesPage';

const PRICE_LABEL = '$195 USD/month';
const PRICE_SHORT = '$195/month';
const BKOS_URL = 'https://bkos.io';
// BKOS legal pages are served by the WORKER (legal-pages-routes.ts), not this
// SPA — a relative href here 404s on the taker host. get.thesuccessfulbookkeeper.com
// is the branded worker hostname (never link the raw workers.dev origin).
const LEGAL_PAGES_BASE = 'https://get.thesuccessfulbookkeeper.com';

// What's inside BKOS — the four pillars of the platform.
const BKOS_PILLARS = [
  { letter: 'T', word: 'Templates', body: 'Your complete Pure Bookkeeping template library, in one place and ready to use — not customised, out-of-date templates that always need updating.' },
  { letter: 'W', word: 'Workflows', body: 'The proven, step-by-step workflows so the same job gets done the same way every time, without you in the room.' },
  { letter: 'S', word: 'Systems', body: 'The dynamically updated SOPs and procedures that turn how your firm works into something your team can repeat and you can trust.' },
  { letter: 'P', word: 'Platform', body: 'One home for all of it at bkos.io — always current, always with you, on any device.' },
];

// What the license includes — the concrete grant.
const INCLUDES = [
  { tag: 'Your firm', title: 'A seat for your whole firm', body: 'Your license covers the firm, not one person — so BKOS becomes the operating system your whole team works from.' },
  { tag: 'The library', title: 'The Pure Bookkeeping system', body: 'Every Pure Bookkeeping template, workflow, and SOP — maintained as a living system, not a binder or external software you keep up yourself.' },
];

const FIT_ITEMS = [
  "You're a Pure Bookkeeping firm and your templates are scattered across folders and tools",
  "You're running outdated session checklists on external software and want a cleaner, permanent home",
  'You want your templates and workflows in one place your whole team can use',
  'You want the system kept current without rebuilding it yourself',
  'You want to be part of the build — new features added steadily, based on what you tell us you need',
  'You want to move from basic workflow software to a complete operating system that handles everything from lead pipelines, intake forms, proposals, engagement letters, client portal, team communication, course builds, internal SOPs — even your website and lead-gen pages',
];

const HOW_IT_WORKS = [
  { n: '1', title: 'Start your license', body: `One simple subscription — ${PRICE_LABEL}. Cancel anytime.` },
  { n: '2', title: "We set up your firm's seat", body: 'Right after checkout our team sets up your BKOS seat by hand and sends your bkos.io login by email.' },
  { n: '3', title: 'Run your firm on BKOS', body: 'Your templates, workflows, and SOPs in one home — ready to put to work the same week. From there you move beyond basic workflow software into a complete operating system: lead pipelines, intake forms, proposals, engagement letters, client portal, team communication, course builds, internal SOPs — even your website and lead-gen pages.' },
];

const FAQS = [
  {
    q: 'What is BKOS?',
    a: 'BKOS is an operating system designed by bookkeepers, for bookkeepers — the platform at bkos.io that holds the Pure Bookkeeping templates, workflows, and SOPs in one place your whole firm can use.',
  },
  {
    q: 'How is this different from what I already have?',
    a: 'Same Pure Bookkeeping system — one living home for it. Instead of templates in folders and workflows in an ageing tool, BKOS keeps it all in one place, on any device, kept up to date for you.',
  },
  {
    q: 'I had Pure Workflow or Pixie with my Pure Bookkeeping license — do I have to pay for BKOS?',
    a: "No. If your Pure Bookkeeping license already included Pure Workflow or Pixie, BKOS is included for you at no extra cost — there's nothing new to buy. We'll help you move everything across: our founding cohort runs the migration as recorded co-working sessions everyone can follow, a second cohort will repeat and refine the process, and a short self-implementation course is coming soon. Firms whose license didn't include Pure Workflow or Pixie start with the $195/month BKOS License.",
  },
  {
    q: `Is the ${PRICE_SHORT} a subscription?`,
    a: 'Yes — BKOS is a monthly subscription for your firm. You keep access for as long as your license is active, and you can cancel anytime.',
  },
  {
    q: 'How soon can I get in after I subscribe?',
    a: 'Right after checkout our team sets up your firm’s BKOS seat by hand and sends your bkos.io login by email — usually within one business day, often much sooner.',
  },
  {
    q: 'Is the license for me or my whole firm?',
    a: 'Your license is firm-level — it covers your practice, not a single person, so BKOS becomes the operating system your team works from.',
  },
  {
    q: 'Can I cancel?',
    a: 'Yes. There is no long-term commitment — your license continues month to month and you can cancel whenever you need to.',
  },
];

// ────────────────────────────────────────────────────────────────────────────
// Buyer-intent capture → order page (same path the Clients / SODA flows use)
// ────────────────────────────────────────────────────────────────────────────

function BuyerIntentModal(props: {
  open: boolean;
  onClose: () => void;
  onSubmit: (d: { email: string; firstName: string; lastName: string }) => void;
}) {
  const { open, onClose, onSubmit } = props;
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setFirstName('');
      setLastName('');
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handle = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email || !firstName) {
      setError('First name and email are required.');
      return;
    }
    setSubmitting(true);
    onSubmit({ email: email.trim(), firstName: firstName.trim(), lastName: lastName.trim() });
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <button style={modalCloseBtn} onClick={onClose} aria-label="Close">×</button>
        <p style={modalEyebrow}>BKOS License · {PRICE_LABEL}</p>
        <h2 style={modalH2}>Where should we send your checkout?</h2>
        <p style={modalSub}>
          Your name and email so the checkout pre-fills — and so we know where to send your
          BKOS login right after you subscribe.
        </p>
        <form onSubmit={handle} style={modalForm}>
          <div style={modalRow}>
            <label style={modalLabel}>
              First name
              <input type="text" required autoFocus value={firstName}
                onChange={(e) => setFirstName(e.target.value)} style={modalInput} />
            </label>
            <label style={modalLabel}>
              Last name
              <input type="text" value={lastName}
                onChange={(e) => setLastName(e.target.value)} style={modalInput} />
            </label>
          </div>
          <label style={modalLabel}>
            Email
            <input type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} style={modalInput} />
          </label>
          {error && <p style={modalError}>{error}</p>}
          <button type="submit" disabled={submitting} style={modalSubmit}>
            {submitting ? 'One sec…' : 'Continue to checkout →'}
          </button>
          <p style={modalFineprint}>
            Continuing takes you to the secure checkout. Your card is charged through Spiffy —
            not stored here.
          </p>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export default function BkosLicensePage({
  slug,
  data,
}: {
  slug: string;
  data: ProgramPublicResponse;
}) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const PASSTHROUGH_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'code',
  ];

  const handleIntent = (payload: { email: string; firstName: string; lastName: string }) => {
    const inbound = new URLSearchParams(window.location.search);
    const out = new URLSearchParams({
      em: payload.email,
      fn: payload.firstName,
      ln: payload.lastName,
    });
    for (const k of PASSTHROUGH_KEYS) {
      const v = inbound.get(k);
      if (v) out.set(k, v);
    }
    navigate(`/programs/${slug}/order?${out.toString()}`);
  };

  const openBuy = () => setModalOpen(true);

  // ── Document title — use the resolved brand name (PB), not the static
  //    TSB shell title, so the browser tab reads as Pure Bookkeeping. ──
  useEffect(() => {
    const brandName = data.brand?.name ?? 'Pure Bookkeeping';
    document.title = data.program?.name ? `${data.program.name} — ${brandName}` : brandName;
    return () => { document.title = brandName; };
  }, [data.program?.name, data.brand?.name]);

  // ── Brand favicon — swap the TSB shell favicon for the brand's own
  //    (brands/pb/favicon.svg). Restores the previous icon on unmount. ──
  useEffect(() => {
    const url = data.brand?.faviconUrl;
    if (!url) return;
    const head = document.head;
    let icon = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const prev = icon?.href ?? null;
    if (!icon) {
      icon = document.createElement('link');
      icon.rel = 'icon';
      head.appendChild(icon);
    }
    icon.href = url;
    return () => {
      const current = head.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (current) current.href = prev ?? '/favicon.svg';
    };
  }, [data.brand?.faviconUrl]);

  return (
    <div style={pageWrap}>
      <style>{`
        .bkos-pillar-letter { font-family: ${FONT_DISPLAY}; }
        .bkos-fit-item::before { content: '✓'; margin-right: 10px; color: ${PROG_PRIMARY}; font-weight: 700; }
      `}</style>

      {/* ── Top nav ── */}
      <header style={topNav}>
        <div style={topNavInner}>
          {data.brand?.logoUrl && (
            <a href="https://www.purebookkeeping.com" style={topNavLogoLink}>
              <img src={data.brand.logoUrl} alt={data.brand.name ?? 'Pure Bookkeeping'} style={topNavLogo} />
            </a>
          )}
          <div style={topNavRight}>
            <span style={topNavMeta}>Firm license · {PRICE_LABEL}</span>
            <button onClick={openBuy} style={navCta}>Get your license</button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section style={heroSection}>
        <div style={heroInner}>
          <p style={heroEyebrow}>For Pure Bookkeeping firms</p>
          <h1 style={heroH1}>Your Pure Bookkeeping system, finally in one living home.</h1>
          <p style={heroTagline}>
            The templates, workflows, and SOPs you already run on — now in one place that stays
            current for you.
          </p>
          <p style={heroSub}>
            BKOS is the home your Pure Bookkeeping system was built for. Everything you've relied on —
            the templates, the workflows, the way the work gets done — in one connected platform your
            whole firm can use, kept up to date for you instead of customised, ageing files you have
            to maintain yourself.
          </p>
          <div style={heroCtaRow}>
            <button onClick={openBuy} style={primaryCta}>
              Get your BKOS License — {PRICE_LABEL}
            </button>
          </div>
          <p style={heroFineprint}>
            A firm-level subscription. Your bkos.io seat is set up for you after checkout. Cancel anytime.
          </p>
        </div>
      </section>

      {/* ── The problem ── */}
      <section style={sectionPale}>
        <div style={sectionInner}>
          <p style={sectionLabel}>The Pure Bookkeeping Firm Owner's Problem</p>
          <h2 style={h2}>You already have the system. It's just not all in one place — and most likely out of date.</h2>
          <p style={lede}>
            Your Pure Bookkeeping templates live in folders. You're running outdated session
            checklists on external software that hasn't been updated or tied to your SOPs in a long
            time. The "how we do it" lives in your head. Every new hire means piecing it back
            together, and keeping it all current keeps landing on you. BKOS brings the whole system
            into one home — a fully connected platform with dynamic updates and the manuals on hand
            right inside each procedure — the Pure Bookkeeping way, ready to run, kept current for you.
          </p>
        </div>
      </section>

      {/* ── What's inside BKOS ── */}
      <section style={section}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>What's Inside BKOS</p>
            <h2 style={h2}>Everything your firm runs on, in one operating system</h2>
            <p style={{ ...lede, maxWidth: '620px', margin: '0 auto 44px' }}>
              Not another tool to manage — the system your practice already wants to be, in one place.
            </p>
          </div>
          <div style={pillarGrid}>
            {BKOS_PILLARS.map((p) => (
              <div key={p.letter} style={pillarCard}>
                <span style={pillarLetter} className="bkos-pillar-letter">{p.letter}</span>
                <p style={pillarWord}>{p.word}</p>
                <p style={pillarBody}>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What the license includes ── */}
      <section style={sectionNavy} id="offer">
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabelLight}>What Your License Includes</p>
            <h2 style={h2Inverse}>One firm-level seat on BKOS — and everything in it</h2>
            <p style={ledeInverse}>
              A simple monthly subscription that puts the whole Pure Bookkeeping system at your firm's
              fingertips, on bkos.io.
            </p>
          </div>
          <div style={offerGrid}>
            {INCLUDES.map((it) => (
              <div key={it.title} style={offerCard}>
                <span style={offerTag}>{it.tag}</span>
                <h3 style={offerCardH3}>{it.title}</h3>
                <p style={offerCardP}>{it.body}</p>
              </div>
            ))}
          </div>
          <div style={takeawayCard}>
            <span style={takeawayTag}>Where it lives</span>
            <p style={takeawayText}>
              Your BKOS platform at bkos.io — always current, always with you. Your firm's seat is
              set up for you after checkout.
            </p>
          </div>
          <div style={facilitatorRow}>
            <button onClick={openBuy} style={primaryCta}>
              Get your BKOS License — {PRICE_LABEL}
            </button>
          </div>
        </div>
      </section>

      {/* ── Proof / launch story (BKOS is brand new — no borrowed community stats) ── */}
      <section style={sectionTint}>
        <div style={{ ...sectionInner, maxWidth: '760px', textAlign: 'center' }}>
          <p style={sectionLabel}>Brand New — Built On What Works</p>
          <h2 style={h2}>The start of something Pure Bookkeeping firms have been waiting for</h2>
          <p style={{ ...lede, fontSize: '19px', margin: '0 auto 20px' }}>
            BKOS is brand new — and built on the Pure Bookkeeping system firms have trusted for
            years, finally brought together into one connected platform. When we unveiled it at the
            Town Hall, the response was immediate: the energy in the room was unmistakable, and our
            founding cohort filled to a wait list.
          </p>
          <p style={storyEmphasis}>
            This is the start of something Pure Bookkeeping firms have been waiting a long time for —
            and you can be part of it from the beginning.
          </p>
        </div>
      </section>

      {/* ── Is this for you ── */}
      <section style={section}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Is This For You?</p>
            <h2 style={h2}>If any of this sounds like your firm, BKOS is built for you</h2>
          </div>
          <ul style={fitList}>
            {FIT_ITEMS.map((item, i) => (
              <li key={i} style={fitItem} className="bkos-fit-item">{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── How it works ── */}
      <section style={sectionPale}>
        <div style={sectionInner}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>How It Works</p>
            <h2 style={h2}>From checkout to your operating system, in three steps</h2>
          </div>
          <div style={stepGrid}>
            {HOW_IT_WORKS.map((s) => (
              <div key={s.n} style={stepCard}>
                <span style={stepNum}>{s.n}</span>
                <p style={stepTitle}>{s.title}</p>
                <p style={stepBody}>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={section}>
        <div style={{ ...sectionInner, maxWidth: '720px' }}>
          <div style={{ textAlign: 'center' }}>
            <p style={sectionLabel}>Questions</p>
            <h2 style={h2}>Before you start your license</h2>
          </div>
          <div style={faqList}>
            {FAQS.map((f, i) => (
              <div key={i} style={faqItem}>
                <p style={faqQ}>{f.q}</p>
                <p style={faqA}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ── */}
      <section style={finalSection}>
        <div style={sectionInner}>
          <p style={finalPre}>Bring your firm's systems into one place — the Pure Bookkeeping way.</p>
          <h2 style={h2Inverse}>Start your BKOS License and run your practice on one operating system.</h2>
          <div style={heroCtaRow}>
            <button onClick={openBuy} style={primaryCta}>
              Get your BKOS License — {PRICE_LABEL}
            </button>
          </div>
          <p style={finalReassure}>
            A firm-level monthly subscription. Your {BKOS_URL.replace('https://', '')} seat is set up
            for you after checkout, and you can cancel anytime.
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={footer}>
        <p style={footerMeta}>
          The BKOS License · A firm operating system powered by Pure Bookkeeping · {PRICE_LABEL}
        </p>
        <p style={footerLinks}>
          <a href={`${LEGAL_PAGES_BASE}/terms`} style={footerLink}>Terms</a>
          <span style={footerDot}>·</span>
          <a href={`${LEGAL_PAGES_BASE}/privacy`} style={footerLink}>Privacy</a>
          <span style={footerDot}>·</span>
          <a href={`${LEGAL_PAGES_BASE}/cookies`} style={footerLink}>Cookies</a>
          <span style={footerDot}>·</span>
          <a href={`${LEGAL_PAGES_BASE}/security`} style={footerLink}>Security</a>
          <span style={footerDot}>·</span>
          <a href={`${LEGAL_PAGES_BASE}/vulnerability-disclosure`} style={footerLink}>Vulnerability Disclosure</a>
        </p>
      </footer>

      <BuyerIntentModal open={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleIntent} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tokens (consumed from <html> custom props set by ProgramSalesPage; fallbacks
// are the Pure Bookkeeping palette so the page is sane even before tokens apply)
// ────────────────────────────────────────────────────────────────────────────

const PROG_PRIMARY = 'var(--prog-primary, #863FFF)';
const PROG_DEEP = 'var(--prog-deep, #2A1B4A)';
const PROG_TEXT_DARK = 'var(--prog-text-dark, #0A0A2E)';
const PROG_TINT = 'var(--prog-tint, #E8E8FF)';
const PROG_HIGHLIGHT = 'var(--prog-highlight, #FF6B35)';
const PROG_CANVAS = 'var(--prog-canvas, #F8F6FB)';
const FONT_DISPLAY = 'var(--font-heading, "Poppins", -apple-system, sans-serif)';
const FONT_BODY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';

const pageWrap: React.CSSProperties = {
  background: '#FFFFFF',
  color: PROG_TEXT_DARK,
  fontFamily: FONT_BODY,
  fontSize: '17px',
  lineHeight: 1.6,
};

// Top nav
const topNav: React.CSSProperties = {
  background: '#FFFFFF',
  borderBottom: '1px solid rgba(10,10,46,0.08)',
  padding: '12px 24px',
  position: 'sticky',
  top: 0,
  zIndex: 50,
};
const topNavInner: React.CSSProperties = {
  maxWidth: '1080px', margin: '0 auto', display: 'flex',
  alignItems: 'center', justifyContent: 'space-between', gap: '16px',
};
const topNavLogoLink: React.CSSProperties = { display: 'inline-flex', alignItems: 'center' };
const topNavLogo: React.CSSProperties = { height: '34px', width: 'auto', display: 'block' };
const topNavRight: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '18px' };
const topNavMeta: React.CSSProperties = {
  fontSize: '12.5px', fontWeight: 600, color: PROG_TEXT_DARK, opacity: 0.7,
};
const navCta: React.CSSProperties = {
  background: PROG_PRIMARY, color: '#fff', fontFamily: FONT_BODY, fontSize: '13px',
  fontWeight: 700, padding: '9px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer',
};

// Sections
const section: React.CSSProperties = { padding: '76px 24px' };
const sectionPale: React.CSSProperties = { ...section, background: PROG_CANVAS };
const sectionTint: React.CSSProperties = { ...section, background: PROG_TINT };
const sectionNavy: React.CSSProperties = { ...section, background: PROG_DEEP };
const sectionInner: React.CSSProperties = { maxWidth: '960px', margin: '0 auto' };

const sectionLabel: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
  color: PROG_PRIMARY, margin: '0 0 12px',
};
const sectionLabelLight: React.CSSProperties = { ...sectionLabel, color: PROG_HIGHLIGHT };

const h2: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: 'clamp(26px, 3.4vw, 36px)', fontWeight: 700,
  lineHeight: 1.15, letterSpacing: '-0.015em', margin: '0 0 18px', color: PROG_TEXT_DARK,
};
const h2Inverse: React.CSSProperties = { ...h2, color: '#FFFFFF' };

const lede: React.CSSProperties = {
  fontSize: '18px', lineHeight: 1.6, color: PROG_TEXT_DARK, opacity: 0.85, margin: '0 0 8px',
};
const ledeInverse: React.CSSProperties = {
  ...lede, color: 'rgba(255,255,255,0.82)', opacity: 1, maxWidth: '620px', margin: '0 auto',
};
const storyEmphasis: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '19px', fontWeight: 700, lineHeight: 1.4,
  letterSpacing: '-0.01em', color: PROG_PRIMARY, maxWidth: '640px', margin: '0 auto',
};

// Hero
const heroSection: React.CSSProperties = {
  padding: '92px 24px 84px',
  background: `linear-gradient(160deg, ${PROG_CANVAS} 0%, ${PROG_TINT} 100%)`,
  borderBottom: '1px solid rgba(10,10,46,0.06)',
};
const heroInner: React.CSSProperties = { maxWidth: '880px', margin: '0 auto', textAlign: 'center' };
const heroEyebrow: React.CSSProperties = {
  fontSize: '12px', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
  color: PROG_PRIMARY, margin: '0 0 18px',
};
const heroH1: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: 'clamp(34px, 5.2vw, 56px)', fontWeight: 800,
  lineHeight: 1.05, letterSpacing: '-0.025em', margin: '0 0 18px', color: PROG_TEXT_DARK,
};
const heroTagline: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: 'clamp(18px, 2.2vw, 23px)', fontWeight: 600,
  lineHeight: 1.3, letterSpacing: '-0.01em', color: PROG_PRIMARY,
  maxWidth: '680px', margin: '0 auto 20px',
};
const heroSub: React.CSSProperties = {
  fontSize: '19px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.82,
  maxWidth: '640px', margin: '0 auto 32px',
};
const heroCtaRow: React.CSSProperties = {
  display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '14px',
};
const primaryCta: React.CSSProperties = {
  background: PROG_PRIMARY, color: '#fff', fontFamily: FONT_BODY, fontSize: '17px',
  fontWeight: 700, padding: '16px 30px', border: 'none', borderRadius: '10px',
  cursor: 'pointer', boxShadow: '0 8px 24px rgba(134,63,255,0.26)',
};
const heroFineprint: React.CSSProperties = {
  fontSize: '14px', color: PROG_TEXT_DARK, opacity: 0.6, margin: '22px 0 0',
};

// Pillars
const pillarGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '20px',
};
const pillarCard: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(10,10,46,0.08)', borderRadius: '18px',
  padding: '28px 24px', boxShadow: '0 2px 14px rgba(10,10,46,0.04)',
};
const pillarLetter: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '52px', height: '52px', borderRadius: '14px', background: PROG_TINT,
  color: PROG_PRIMARY, fontSize: '26px', fontWeight: 700, marginBottom: '16px',
};
const pillarWord: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '21px', fontWeight: 700, letterSpacing: '-0.01em',
  margin: '0 0 8px', color: PROG_TEXT_DARK,
};
const pillarBody: React.CSSProperties = {
  fontSize: '15px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.78, margin: 0,
};

// Offer (navy)
const offerGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '20px', margin: '44px 0 28px',
};
const offerCard: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '18px', padding: '28px 26px',
};
const offerTag: React.CSSProperties = {
  display: 'inline-block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: PROG_HIGHLIGHT, marginBottom: '12px',
};
const offerCardH3: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '23px', fontWeight: 700, letterSpacing: '-0.01em',
  margin: '0 0 10px', color: '#fff',
};
const offerCardP: React.CSSProperties = {
  fontSize: '15.5px', lineHeight: 1.6, color: 'rgba(255,255,255,0.78)', margin: 0,
};
const takeawayCard: React.CSSProperties = {
  background: 'rgba(255,107,53,0.10)', border: `1px solid ${PROG_HIGHLIGHT}`,
  borderRadius: '16px', padding: '22px 26px', margin: '0 0 32px', textAlign: 'center',
};
const takeawayTag: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em',
  textTransform: 'uppercase', color: PROG_HIGHLIGHT, marginBottom: '8px',
};
const takeawayText: React.CSSProperties = {
  fontSize: '16.5px', lineHeight: 1.5, color: 'rgba(255,255,255,0.92)', margin: 0,
};
const facilitatorRow: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px', textAlign: 'center',
};

// Fit
const fitList: React.CSSProperties = {
  listStyle: 'none', padding: 0, margin: '36px auto 0', maxWidth: '560px',
  display: 'flex', flexDirection: 'column', gap: '14px',
};
const fitItem: React.CSSProperties = {
  fontSize: '17px', lineHeight: 1.45, color: PROG_TEXT_DARK, display: 'flex', alignItems: 'baseline',
};

// How it works (3 steps)
const stepGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: '20px', margin: '44px 0 0',
};
const stepCard: React.CSSProperties = {
  background: '#fff', border: '1px solid rgba(10,10,46,0.08)', borderRadius: '18px',
  padding: '26px 24px', textAlign: 'center', boxShadow: '0 2px 14px rgba(10,10,46,0.04)',
};
const stepNum: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '40px', height: '40px', borderRadius: '50%', background: PROG_PRIMARY,
  color: '#fff', fontSize: '18px', fontWeight: 700, marginBottom: '14px',
};
const stepTitle: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '18px', fontWeight: 700, letterSpacing: '-0.01em',
  color: PROG_TEXT_DARK, margin: '0 0 8px',
};
const stepBody: React.CSSProperties = {
  fontSize: '14.5px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.78, margin: 0,
};

// FAQ
const faqList: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '16px', margin: '36px 0 0',
};
const faqItem: React.CSSProperties = {
  background: PROG_CANVAS, border: '1px solid rgba(10,10,46,0.07)', borderRadius: '14px',
  padding: '20px 22px',
};
const faqQ: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '17px', fontWeight: 700, letterSpacing: '-0.01em',
  color: PROG_TEXT_DARK, margin: '0 0 8px',
};
const faqA: React.CSSProperties = {
  fontSize: '15.5px', lineHeight: 1.6, color: PROG_TEXT_DARK, opacity: 0.82, margin: 0,
};

// Final
const finalSection: React.CSSProperties = {
  padding: '84px 24px', background: PROG_DEEP, textAlign: 'center',
};
const finalPre: React.CSSProperties = {
  fontSize: '15px', fontWeight: 600, letterSpacing: '0.04em', color: PROG_HIGHLIGHT, margin: '0 0 16px',
};
const finalReassure: React.CSSProperties = {
  fontSize: '15px', lineHeight: 1.55, color: 'rgba(255,255,255,0.78)',
  maxWidth: '520px', margin: '22px auto 0',
};

// Footer
const footer: React.CSSProperties = {
  padding: '36px 24px', textAlign: 'center', background: '#fff',
  borderTop: '1px solid rgba(10,10,46,0.08)',
};
const footerMeta: React.CSSProperties = {
  fontSize: '13px', color: PROG_TEXT_DARK, opacity: 0.6, margin: '0 0 10px',
};
const footerLinks: React.CSSProperties = {
  fontSize: '12px', color: PROG_TEXT_DARK, opacity: 0.5, margin: 0,
};
const footerLink: React.CSSProperties = {
  color: 'inherit', textDecoration: 'none',
};
const footerDot: React.CSSProperties = {
  margin: '0 6px',
};

// Modal
const modalOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(10,10,46,0.55)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', padding: '20px', zIndex: 100,
};
const modalCard: React.CSSProperties = {
  background: '#fff', borderRadius: '18px', padding: '34px 32px', maxWidth: '440px',
  width: '100%', position: 'relative', boxShadow: '0 24px 60px rgba(10,10,46,0.3)',
};
const modalCloseBtn: React.CSSProperties = {
  position: 'absolute', top: '14px', right: '16px', background: 'none', border: 'none',
  fontSize: '26px', lineHeight: 1, color: PROG_TEXT_DARK, opacity: 0.4, cursor: 'pointer',
};
const modalEyebrow: React.CSSProperties = {
  fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: PROG_PRIMARY, margin: '0 0 10px',
};
const modalH2: React.CSSProperties = {
  fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 700, letterSpacing: '-0.015em',
  margin: '0 0 10px', color: PROG_TEXT_DARK,
};
const modalSub: React.CSSProperties = {
  fontSize: '15px', lineHeight: 1.55, color: PROG_TEXT_DARK, opacity: 0.75, margin: '0 0 22px',
};
const modalForm: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '14px' };
const modalRow: React.CSSProperties = { display: 'flex', gap: '12px', flexWrap: 'wrap' };
const modalLabel: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px',
  fontWeight: 600, color: PROG_TEXT_DARK, flex: 1, minWidth: '140px',
};
const modalInput: React.CSSProperties = {
  padding: '11px 13px', fontSize: '15px', border: '1px solid #D1D1D6', borderRadius: '8px',
  fontFamily: FONT_BODY, color: PROG_TEXT_DARK, fontWeight: 400,
};
const modalError: React.CSSProperties = { color: '#B91C1C', fontSize: '13px', margin: 0 };
const modalSubmit: React.CSSProperties = {
  background: PROG_PRIMARY, color: '#fff', fontFamily: FONT_BODY, fontSize: '16px',
  fontWeight: 700, padding: '14px', border: 'none', borderRadius: '10px', cursor: 'pointer', marginTop: '4px',
};
const modalFineprint: React.CSSProperties = {
  fontSize: '12px', lineHeight: 1.5, color: PROG_TEXT_DARK, opacity: 0.55, margin: 0, textAlign: 'center',
};
